import * as Sentry from "@sentry/react";
import type { UploadsAPIService, CreateUploadResponse } from "@rails_api_base";
import { resizeImage } from "./imageResize";

// Structural safety rail against runaway parallelism if someone drops
// an absurd number of files. 
const MAX_CONCURRENT_RESIZES = 15;

class Semaphore {
  private active = 0;
  private readonly queue: Array<() => void> = [];

  constructor(private readonly limit: number) {}

  async run<T>(fn: () => Promise<T>): Promise<T> {
    if (this.active >= this.limit) {
      await new Promise<void>((resolve) => this.queue.push(resolve));
    }
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      const next = this.queue.shift();
      if (next) next();
    }
  }
}

const resizeQueue = new Semaphore(MAX_CONCURRENT_RESIZES);

export interface UploadPipelineOptions {
  isLogo?: boolean;
  websiteId?: number;
  maxDimension?: number;
}

/**
 * The one and only client upload pipeline. Every caller (brand panel,
 * chat composer, future paths) MUST go through this function so that
 * concurrency control, error reporting, and the create→presign→confirm
 * sequencing live in one place.
 *
 * 1. Resize the original to a preview (throttled by `resizeQueue`)
 * 2. POST the preview to Rails (`service.create`) — awaited
 * 3. Kick off the full-res R2 upload (`uploadOriginalInBackground`) —
 *    fire-and-forget; failures there are reported to Sentry but do not
 *    fail the pipeline because the preview is already persisted.
 */
export async function uploadFile(
  service: UploadsAPIService,
  file: File,
  opts: UploadPipelineOptions = {}
): Promise<CreateUploadResponse> {
  const resized = await resizeQueue.run(() => resizeImage(file, opts.maxDimension ?? 1200));

  const response = await service.create({
    file: resized,
    isLogo: opts.isLogo,
    websiteId: opts.websiteId,
  });

  // Fire-and-forget the full-res upload. We no longer swallow errors inside
  // the service — every failure lands in Sentry tagged with the last stage
  // reached, so presign vs R2 PUT vs confirm can be told apart at a glance.
  let lastStage: string = "not_started";
  service
    .uploadOriginalInBackground(response.id, file, (stage) => {
      lastStage = stage;
      Sentry.addBreadcrumb({
        category: "upload",
        level: "info",
        message: `background_original.${stage}`,
        data: { uploadId: response.id, filename: file.name, size: file.size },
      });
    })
    .catch((error: unknown) => {
      Sentry.captureException(error, {
        tags: { area: "upload", stage: lastStage },
        extra: {
          uploadId: response.id,
          filename: file.name,
          size: file.size,
          contentType: file.type,
        },
      });
    });

  return response;
}
